import { useState, useEffect, useRef, useMemo } from 'react';
import { Form } from 'antd';
import { FolderOutlined, FolderOpenOutlined, FileOutlined } from '@ant-design/icons';
import './Horizontal.scss';

const renderDescription = (leafNode, disabled) => {
  const configDefinition = leafNode?.configDefinition;
  return configDefinition ? (
    <div className={`description selected ${disabled ? 'disabled' : ''}`}>
      <p><strong>Name:</strong> {configDefinition.name}</p>
      <p><strong>Version:</strong> {configDefinition.version}</p>
      <p><strong>Description:</strong> {configDefinition.description}</p>
    </div>
  ) : (
    <div className={`description ${disabled ? 'disabled' : ''}`}>
      <p className="placeholder">Select a functionality to see its description.</p>
    </div>
  );
};

const Horizontal = ({
  treeData,
  onLeafNodeSelect,
  form,
  disabled = false,
  isNodeDisabled,
  ...props
}) => {
  const [selectedPath, setSelectedPath] = useState([]);
  const [leafNode, setLeafNode] = useState(null);
  const prevLeafNodeRef = useRef(null);
  const [expandedNodesMap, setExpandedNodesMap] = useState({});

  const nodeIsDisabled = (node) =>
    !!(node?.disabled || (typeof isNodeDisabled === 'function' && isNodeDisabled(node)));

  const getNodesAtLevel = (nodes, levelPath) => {
    return levelPath.reduce((currentNodes, name) => {
      const selectedNode = currentNodes.find((node) => node.name === name);
      return selectedNode?.children ?? [];
    }, nodes);
  };

  const getPathFromNode = (nodes, targetName, targetVersion, path = []) => {
    for (const node of nodes) {
      const newPath = [...path, node.name];
      const { name, version } = node.configDefinition || {};
      if (version === targetVersion && (name === targetName || node.name === targetName)) {
        return newPath;
      }
      if (node.children?.length > 0) {
        const foundPath = getPathFromNode(node.children, targetName, targetVersion, newPath);
        if (foundPath.length) return foundPath;
      }
    }
    return [];
  };

  const selectNode = (level, nodeName) => {
    if (disabled) return;

    setSelectedPath((prev) => {
      const newPath = [...prev.slice(0, level), nodeName];
      const nodesAtCurrentLevel = getNodesAtLevel(treeData, newPath.slice(0, -1));
      const selectedNode = nodesAtCurrentLevel.find((node) => node.name === nodeName);

      if (selectedNode && nodeIsDisabled(selectedNode)) {
        return prev;
      }

      const isLeaf = selectedNode && (!selectedNode.children || selectedNode.children.length === 0);
      setLeafNode(isLeaf ? selectedNode : null);
      if (isLeaf && onLeafNodeSelect) onLeafNodeSelect(selectedNode);

      setExpandedNodesMap((prevExpanded) => ({
        ...prevExpanded,
        [level]: nodeName,
      }));
      return newPath;
    });
  };

  useEffect(() => {
    if (!disabled && leafNode && prevLeafNodeRef.current !== leafNode) {
      prevLeafNodeRef.current = leafNode;
      form.setFieldsValue({ functionality: leafNode });
    }
  }, [leafNode, form, disabled]);

  useEffect(() => {
    const functionality = form.getFieldValue('functionality');
    if (functionality && !prevLeafNodeRef.current) {
      const path = getPathFromNode(
        treeData,
        functionality.configDefinition?.name,
        functionality.configDefinition?.version
      );
      if (path.length) {
        setSelectedPath(path);
        setLeafNode(functionality);
        setExpandedNodesMap(path.reduce((acc, nodeName, index) => ({ ...acc, [index]: nodeName }), {}));
      }
    }
  }, [form, treeData]);

  const columns = useMemo(() => {
    return [treeData, ...selectedPath].reduce((cols, _, level) => {
      const currentPath = selectedPath.slice(0, level);
      const nodes = getNodesAtLevel(treeData, currentPath);
      if (nodes.length) {
        cols.push(
          <div key={level} className="column">
            {nodes.map((node) => {
              const isSelected = selectedPath[level] === node.name;
              const isDisabled = disabled || nodeIsDisabled(node);
              const isFolder = !!node.children?.length;
              const Icon = isFolder
                ? (expandedNodesMap[level] === node.name ? FolderOpenOutlined : FolderOutlined)
                : FileOutlined;

              const handleActivate = () => {
                if (!isDisabled) selectNode(level, node.name);
              };

              const onKeyDown = (e) => {
                if (isDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectNode(level, node.name);
                }
              };

              return (
                <div
                  key={node.name}
                  role="button"
                  aria-disabled={isDisabled}
                  tabIndex={isDisabled ? -1 : 0}
                  className={`node ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={handleActivate}
                  onKeyDown={onKeyDown}
                >
                  <span className="icon"><Icon /></span>
                  <span>{node.name}</span>
                </div>
              );
            })}
          </div>
        );
      }
      return cols;
    }, []);
  }, [treeData, selectedPath, expandedNodesMap, disabled, isNodeDisabled]);

  return (
    <>
      <Form.Item name="functionality" noStyle />
      <Form.Item
        label="Functionality"
        {...props}
      >
        <div className={`functionality-tree-container ${disabled ? 'is-disabled' : ''}`}>
          <div className="tree">
            {columns}
          </div>
          {renderDescription(leafNode, disabled)}
        </div>
      </Form.Item>
    </>
  );
};

export default Horizontal;
